import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { AdminLayout } from "@/components/admin-layout";
import { Plus, BookOpen, Building2, Loader2 } from "lucide-react";

export default function AdminCoaTemplatesPage() {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [showApply, setShowApply] = useState(false);
  const [newName, setNewName] = useState("");
  const [newIndustry, setNewIndustry] = useState("");
  const [newDesc, setNewDesc] = useState("");
  const [applyTemplateId, setApplyTemplateId] = useState("");
  const [applyBusinessId, setApplyBusinessId] = useState("");
  const [applyForce, setApplyForce] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<any>(null);

  const { data: templates = [], isLoading, error: templatesError } = useQuery({
    queryKey: ["/api/admin/coa/templates"],
    queryFn: async () => {
      try {
        const res = await apiRequest("GET", "/api/admin/coa/templates");
        return res.json();
      } catch (e) {
        // Fallback: try user endpoint if admin endpoint fails (auth issue)
        console.warn("Admin templates fetch failed, trying user endpoint:", e);
        const res = await apiRequest("GET", "/api/coa/templates");
        return res.json();
      }
    },
    retry: false,
  });

  const { data: templateDetail } = useQuery({
    queryKey: ["/api/admin/coa/templates", selectedTemplate?.id],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/admin/coa/templates/${selectedTemplate.id}`);
      return res.json();
    },
    enabled: !!selectedTemplate?.id,
  });

  const createMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/coa/templates", { name: newName, industry: newIndustry, description: newDesc });
      return res.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/coa/templates"] });
      toast({ title: "Template created" });
      setShowCreate(false);
      setNewName(""); setNewIndustry(""); setNewDesc("");
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const applyMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/coa/apply-template", {
        templateId: parseInt(applyTemplateId),
        businessId: parseInt(applyBusinessId),
        force: applyForce,
      });
      return res.json();
    },
    onSuccess: (data) => {
      toast({ title: "Template applied", description: `${data.accountsCreated} accounts created` });
      setShowApply(false);
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  const seedMutation = useMutation({
    mutationFn: async () => {
      const res = await apiRequest("POST", "/api/admin/coa/seed");
      return res.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/admin/coa/templates"] });
      toast({ title: "Templates seeded", description: `${data.count} templates available` });
    },
    onError: (err: any) => toast({ title: "Error", description: err.message, variant: "destructive" }),
  });

  return (
    <AdminLayout>
      <div className="max-w-5xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold">COA Templates</h1>
            <p className="text-muted-foreground text-sm">Manage chart of accounts templates for client onboarding</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
              {seedMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : "Seed Defaults"}
            </Button>
            <Button variant="outline" onClick={() => setShowApply(true)}>
              <Building2 className="h-4 w-4 mr-2" /> Apply to Business
            </Button>
            <Button onClick={() => setShowCreate(true)}>
              <Plus className="h-4 w-4 mr-2" /> New Template
            </Button>
          </div>
        </div>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1, 2, 3, 4].map((i) => <Card key={i}><CardContent className="p-6 h-32 animate-pulse bg-muted" /></Card>)}
          </div>
        ) : templates.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center">
              <BookOpen className="h-12 w-12 mx-auto text-muted-foreground mb-3" />
              <p className="text-muted-foreground mb-4">No templates found. Seed the default templates first.</p>
              <Button onClick={() => seedMutation.mutate()} disabled={seedMutation.isPending}>
                {seedMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Seed Default Templates
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.map((t: any) => (
              <Card key={t.id} className={`cursor-pointer transition-shadow hover:shadow-md ${selectedTemplate?.id === t.id ? "ring-2 ring-primary" : ""}`} onClick={() => setSelectedTemplate(t)}>
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-base flex items-center gap-2">
                      <BookOpen className="h-4 w-4" /> {t.name}
                    </CardTitle>
                    <Badge variant="outline">{t.industry}</Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-sm text-muted-foreground">{t.description || "No description"}</p>
                  <div className="mt-2">
                    <Badge variant="secondary" className="text-xs">{t.accountCount || 0} accounts</Badge>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Template detail panel */}
        {templateDetail && (
          <Card>
            <CardHeader>
              <CardTitle>{templateDetail.name} — Accounts ({templateDetail.accounts?.length || 0})</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-64 overflow-y-auto">
                {(templateDetail.accounts || []).map((a: any) => (
                  <div key={a.id || a.code} className="flex items-center gap-2 py-1 text-sm border-b">
                    <span className="font-mono text-xs text-muted-foreground w-10">{a.code}</span>
                    <span>{a.name}</span>
                    <Badge variant="secondary" className="text-xs ml-auto">{a.type}</Badge>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Create Template Dialog */}
        <Dialog open={showCreate} onOpenChange={setShowCreate}>
          <DialogContent>
            <DialogHeader><DialogTitle>Create Template</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Name</Label><Input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="e.g. Restaurant" /></div>
              <div><Label>Industry</Label><Input value={newIndustry} onChange={(e) => setNewIndustry(e.target.value)} placeholder="e.g. restaurant" /></div>
              <div><Label>Description</Label><Input value={newDesc} onChange={(e) => setNewDesc(e.target.value)} placeholder="Chart of accounts for..." /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={() => createMutation.mutate()} disabled={!newName || !newIndustry || createMutation.isPending}>Create</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Apply to Business Dialog */}
        <Dialog open={showApply} onOpenChange={setShowApply}>
          <DialogContent>
            <DialogHeader><DialogTitle>Apply Template to Business</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label>Template ID</Label><Input value={applyTemplateId} onChange={(e) => setApplyTemplateId(e.target.value)} placeholder="1" /></div>
              <div><Label>Business ID</Label><Input value={applyBusinessId} onChange={(e) => setApplyBusinessId(e.target.value)} placeholder="1" /></div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="force" checked={applyForce} onChange={(e) => setApplyForce(e.target.checked)} />
                <label htmlFor="force" className="text-sm">Force (delete existing COA)</label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowApply(false)}>Cancel</Button>
              <Button onClick={() => applyMutation.mutate()} disabled={!applyTemplateId || !applyBusinessId || applyMutation.isPending}>Apply</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
